import { Locale } from "../Locale.ts";

/**
 * UI strings for the printable card game pages (`src/CardGames/**`, ADR 0135). The page chrome
 * only: the rules themselves are in `CardGames/games.ts`, English, and print from there. The
 * pages are English-only for now, so Spanish and French are here to keep the catalogue complete
 * (ADR 0051), not because a localised route exists.
 *
 * The section headings of the rules repeat `CardGames/ruleSections.ts`, which the PDF prints from
 * (it cannot call the translation hook in a worker); `cardGames.test.ts` holds the two together.
 */
const en = {
    "cardGames.printTheGame": "Print the game",
    "cardGames.printSummaryTemplate":
        "{pages} pages: the rules, then {cards} cards on {sheets} sheets, then {records} record sheets, two to a page.",
    "cardGames.printSummaryNoRecordsTemplate": "{pages} pages: the rules, then {cards} cards on {sheets} sheets.",
    "cardGames.printTip": "Card stock works best. Cut along the dashed lines.",
    "cardGames.openPdf": "Open the card game PDF",
    "cardGames.buildingPdf": "Building the card game…",
    "cardGames.previewOfRulesPage": "Preview of the rules page",
    "cardGames.previewTitle": "Card game preview",
    "cardGames.rulesHeadingTemplate": "Rules of {name}",
    "cardGames.goal": "Goal",
    "cardGames.youNeed": "You need",
    "cardGames.setUp": "Set up",
    "cardGames.howToPlay": "How to play",
    "cardGames.example": "Example",
    "cardGames.winning": "Who wins",
    "cardGames.easier": "Make it easier",
    "cardGames.harder": "Make it harder",
    "cardGames.practises": "What it practises",
} as const;

export type CardGamesStringKey = keyof typeof en;

const es = {
    "cardGames.printTheGame": "Imprimir el juego",
    "cardGames.printSummaryTemplate":
        "{pages} páginas: las reglas, luego {cards} cartas en {sheets} hojas, luego {records} hojas de registro, dos por página.",
    "cardGames.printSummaryNoRecordsTemplate": "{pages} páginas: las reglas, luego {cards} cartas en {sheets} hojas.",
    "cardGames.printTip": "Funciona mejor en cartulina. Corta por las líneas de puntos.",
    "cardGames.openPdf": "Abrir el PDF del juego de cartas",
    "cardGames.buildingPdf": "Preparando el juego de cartas…",
    "cardGames.previewOfRulesPage": "Vista previa de la página de reglas",
    "cardGames.previewTitle": "Vista previa del juego de cartas",
    "cardGames.rulesHeadingTemplate": "Reglas de {name}",
    "cardGames.goal": "Objetivo",
    "cardGames.youNeed": "Qué necesitas",
    "cardGames.setUp": "Preparación",
    "cardGames.howToPlay": "Cómo se juega",
    "cardGames.example": "Ejemplo",
    "cardGames.winning": "Quién gana",
    "cardGames.easier": "Más fácil",
    "cardGames.harder": "Más difícil",
    "cardGames.practises": "Qué practica",
} satisfies Record<CardGamesStringKey, string>;

const fr = {
    "cardGames.printTheGame": "Imprimer le jeu",
    "cardGames.printSummaryTemplate":
        "{pages} pages : les règles, puis {cards} cartes sur {sheets} feuilles, puis {records} feuilles de score, deux par page.",
    "cardGames.printSummaryNoRecordsTemplate": "{pages} pages : les règles, puis {cards} cartes sur {sheets} feuilles.",
    "cardGames.printTip": "Le carton épais donne le meilleur résultat. Découpez le long des pointillés.",
    "cardGames.openPdf": "Ouvrir le PDF du jeu de cartes",
    "cardGames.buildingPdf": "Préparation du jeu de cartes…",
    "cardGames.previewOfRulesPage": "Aperçu de la page des règles",
    "cardGames.previewTitle": "Aperçu du jeu de cartes",
    "cardGames.rulesHeadingTemplate": "Règles de {name}",
    "cardGames.goal": "But",
    "cardGames.youNeed": "Il vous faut",
    "cardGames.setUp": "Mise en place",
    "cardGames.howToPlay": "Comment jouer",
    "cardGames.example": "Exemple",
    "cardGames.winning": "Qui gagne",
    "cardGames.easier": "Plus facile",
    "cardGames.harder": "Plus difficile",
    "cardGames.practises": "Ce que cela travaille",
} satisfies Record<CardGamesStringKey, string>;

export const cardGamesStrings: Record<Locale, Record<CardGamesStringKey, string>> = { en, es, fr };
